import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Grid, GridColumn, List } from "semantic-ui-react";

import { typedQueryClient } from "hooks";
import "modules/CheckInServices/CheckInServices.scss";
import { DealerServiceModal, DealerServicesList, LocationServiceModal, LocationServicesList } from "modules/CheckInServices/components";
import { useCheckInServicesQuery } from "modules/CheckInServices/components/DealerServicesList/hooks";
import { useEnableCheckInServiceMutation, useTagsQuery } from "modules/CheckInServices/components/LocationServicesList/hooks";
import { CheckInServicesQueryKeys } from "modules/CheckInServices/queryKeys";

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type { CheckInService } from "models";

export const CheckInServices = () => {
  const { t } = useTranslation();

  const [selectedDealerService, setSelectedDealerService] = useState<CheckInService | null>(null);
  const [selectedLocationService, setSelectedLocationService] = useState<CheckInService | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeService, setActiveService] = useState<CheckInService | null>(null);

  const { mutate: enableService } = useEnableCheckInServiceMutation();
  const { data: dealerServices = [] } = useCheckInServicesQuery();
  const { data: tags = [] } = useTagsQuery();

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const service = dealerServices.find(s => s.id === Number(event.active.id));
    setActiveService(service ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveService(null);
    if (!event.over) return;

    const serviceId = Number(event.active.id);
    const locationServices = typedQueryClient.getQueryData(CheckInServicesQueryKeys.locationList);

    if (locationServices?.some(locationService => locationService.id === serviceId))
      return toast.error(t("v8_service_already_added_to_location", "This service is already added to this location"));

    enableService(serviceId);
  };

  const handleLocationServiceClick = (service: CheckInService) => {
    setSelectedLocationService(service);
    setIsModalOpen(true);
  };

  const handleDealerServiceClick = (service: CheckInService) => {
    setSelectedDealerService(service);
    setIsModalOpen(true);
  };

  const handleAddService = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedDealerService(null);
    setSelectedLocationService(null);
    setIsModalOpen(false);
  };

  const activeTag = tags.find(t => t.id === activeService?.tag_id);

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="CheckInServices">
        <Grid columns={2}>
          <GridColumn>
            <LocationServicesList onServiceClick={handleLocationServiceClick} />
          </GridColumn>
          <GridColumn>
            <DealerServicesList onServiceClick={handleDealerServiceClick} onAddService={handleAddService} />
          </GridColumn>
        </Grid>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeService && (
          <List.Item className="Service drag-overlay">
            <List.Content>
              <div className="service-name-tag">
                <span>{activeService.name}</span>
                {activeTag && (
                  <span className="service-tag" style={{ backgroundColor: activeTag.color }}>
                    {activeTag.name}
                  </span>
                )}
              </div>

              <div className="service-price">{activeService.price}</div>
            </List.Content>
          </List.Item>
        )}
      </DragOverlay>

      {selectedLocationService ? (
        <LocationServiceModal isOpen={isModalOpen} onClose={handleCloseModal} checkInService={selectedLocationService} />
      ) : (
        <DealerServiceModal isOpen={isModalOpen} onClose={handleCloseModal} checkInService={selectedDealerService} />
      )}
    </DndContext>
  );
};
